import { useEffect, useState } from "react";
import { Button } from "../../components/Button/Button";
import { PageLayout } from "../../components/layout/PageLayout";
import { showErrorToast, showSuccessToast } from "../../lib/ToastUtility";
import { useKioskTransactions } from "../../hooks/useKioskTransactions";
import { updateKioskOwnerPreferences } from "../../services/KioskConfigService";
import {
  getOwnerPayoutProfile,
  upsertOwnerPayoutProfile,
} from "../../services/KioskTransactionsService";
import {
  KioskOwnerPreferences,
  OwnerPayoutProfile,
} from "../../types/kioskTransactions.type";

const OwnerReportingPayoutSettingsPage = () => {
  const { kiosks, summary, refreshData } = useKioskTransactions();

  const [ownerPreferencesSaving, setOwnerPreferencesSaving] = useState(false);
  const [loadingPayoutProfile, setLoadingPayoutProfile] = useState(false);
  const [savingPayoutProfile, setSavingPayoutProfile] = useState(false);
  const [ownerPreferencesDraft, setOwnerPreferencesDraft] =
    useState<KioskOwnerPreferences | null>(null);
  const [bankAccountHolderName, setBankAccountHolderName] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [bankSortCode, setBankSortCode] = useState("");
  const [bankIban, setBankIban] = useState("");
  const [bankSwiftBic, setBankSwiftBic] = useState("");
  const [bankCountry, setBankCountry] = useState("");
  const [payoutProfile, setPayoutProfile] = useState<OwnerPayoutProfile | null>(null);
  const ownerPreferences = ownerPreferencesDraft || summary.ownerPreferences;

  const loadPayoutProfile = async () => {
    setLoadingPayoutProfile(true);
    try {
      const profile = await getOwnerPayoutProfile();
      setPayoutProfile(profile);
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to load payout profile");
    } finally {
      setLoadingPayoutProfile(false);
    }
  };

  useEffect(() => {
    void loadPayoutProfile();
  }, []);

  const updateOwnerPreference = <K extends keyof KioskOwnerPreferences>(
    key: K,
    value: KioskOwnerPreferences[K],
  ) => {
    setOwnerPreferencesDraft((previous) => ({
      ...(previous || summary.ownerPreferences),
      [key]: value,
    }));
  };

  const saveOwnerPreferences = async () => {
    const kioskIds = kiosks.map((kiosk) => kiosk.kioskId).filter(Boolean);

    if (kioskIds.length === 0) {
      showErrorToast("No kiosks found for this owner.");
      return;
    }

    const payload = ownerPreferencesDraft || summary.ownerPreferences;
    setOwnerPreferencesSaving(true);
    try {
      await Promise.all(
        kioskIds.map((kioskId) =>
          updateKioskOwnerPreferences(kioskId, {
            reportLiveEnabled: payload.reportLiveEnabled,
            reportWeeklyEnabled: payload.reportWeeklyEnabled,
            reportMonthlyEnabled: payload.reportMonthlyEnabled,
          }),
        ),
      );

      setOwnerPreferencesDraft(null);
      showSuccessToast("Reporting settings saved for all kiosks.");
      await refreshData();
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to save owner preferences");
    } finally {
      setOwnerPreferencesSaving(false);
    }
  };

  const savePayoutProfile = async () => {
    setSavingPayoutProfile(true);
    try {
      await upsertOwnerPayoutProfile({
        methodType: "BANK",
        bankAccountHolderName,
        bankName,
        bankAccountNumber,
        bankSortCode,
        bankIban,
        bankSwiftBic,
        bankCountry,
        stripeAutoPayoutEnabled: false,
      });

      showSuccessToast("Payout profile submitted for admin review.");
      await loadPayoutProfile();
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to save payout profile");
    } finally {
      setSavingPayoutProfile(false);
    }
  };

  return (
    <PageLayout
      title="Account Settings"
      subtitle="Manage notifications and payout setup"
    >
      <div className="p-6 space-y-6">
        <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/80 dark:bg-slate-900/70 p-4 space-y-4">
          <div className="rounded-xl border border-gray-200 dark:border-white/10 p-4 space-y-2">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Reporting Updates</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              These settings are applied to all kiosks owned by the current account.
            </p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={ownerPreferences.reportLiveEnabled}
                onChange={(event) =>
                  updateOwnerPreference("reportLiveEnabled", event.target.checked)
                }
              />
              Live updates
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={ownerPreferences.reportWeeklyEnabled}
                onChange={(event) =>
                  updateOwnerPreference("reportWeeklyEnabled", event.target.checked)
                }
              />
              Weekly updates
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={ownerPreferences.reportMonthlyEnabled}
                onChange={(event) =>
                  updateOwnerPreference("reportMonthlyEnabled", event.target.checked)
                }
              />
              Monthly updates
            </label>
          </div>

          <div className="flex justify-end">
            <Button
              value="Save Preferences"
              onClick={() => {
                void saveOwnerPreferences();
              }}
              loading={ownerPreferencesSaving}
              disabled={kiosks.length === 0}
            />
          </div>
        </div>

        <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/80 dark:bg-slate-900/70 p-4 space-y-4">
          <div className="space-y-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Payout Account Details</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Payout details are encrypted in storage.
            </p>
            {payoutProfile && (
              <p className="text-xs text-emerald-700 dark:text-emerald-300">
                Current profile status: {payoutProfile.status}
                {payoutProfile.reviewNote ? ` (${payoutProfile.reviewNote})` : ""}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Payout Method</label>
            <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-white/10 dark:bg-slate-800 dark:text-gray-200">
              Bank transfer
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input value={bankAccountHolderName} onChange={(e) => setBankAccountHolderName(e.target.value)} placeholder="Account holder name" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800" />
            <input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="Bank name" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800" />
            <input value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value)} placeholder="Account number" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800" />
            <input value={bankSortCode} onChange={(e) => setBankSortCode(e.target.value)} placeholder="Sort code / routing" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800" />
            <input value={bankIban} onChange={(e) => setBankIban(e.target.value)} placeholder="IBAN (optional)" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800" />
            <input value={bankSwiftBic} onChange={(e) => setBankSwiftBic(e.target.value)} placeholder="SWIFT/BIC (optional)" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800" />
            <input value={bankCountry} onChange={(e) => setBankCountry(e.target.value)} placeholder="Country code (e.g. GB)" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-slate-800" />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              value={loadingPayoutProfile ? "Loading..." : "Save Payout Profile"}
              onClick={() => {
                void savePayoutProfile();
              }}
              loading={savingPayoutProfile}
            />
          </div>
        </div>

      </div>
    </PageLayout>
  );
};

export default OwnerReportingPayoutSettingsPage;
